import React from 'react';
import { Bot, Filter, Repeat, Wrench } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { PersistedWorkflow } from '@cotera/sdk/core';
import type { WorkflowGraphNodeFlowRenderProps } from '../../../workflow-render-catalog/types';
import { NODE_ACCENTS, type NodeAccent } from '../../node-accent';
import { WorkflowNodeShell } from '../workflow-node-shell';
import type { WorkflowNodeRenderStrategy } from '../types';
import { asRecord, readString } from '../../utils';
import {
  forEachBodyTypeLabel,
  type ForEachBodySummary,
} from '../edit-strategies/for-each-child-node';

const { WorkflowNodeType } = PersistedWorkflow;

/** Short human description of what a for-each iterates over (fixed list or path). */
function describeForEachItems(nodeData: unknown): string {
  const record = asRecord(nodeData);
  const items = record === null ? undefined : record['items'];
  if (Array.isArray(items) && items.length > 0) {
    const preview = items
      .slice(0, 3)
      .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
      .join(', ');
    const suffix = items.length > 3 ? `, +${items.length - 3}` : '';
    return `Items: ${preview}${suffix}`;
  }
  const itemsPath = readString(record, 'itemsPath');
  return itemsPath !== undefined
    ? `Items: ${itemsPath}`
    : 'Iterate over a collection';
}

function bodySummaryFromNodeData(nodeData: unknown): ForEachBodySummary | null {
  const record = asRecord(nodeData);
  const childNodeId = readString(record, 'childNodeId');
  const bodyType = readString(record, 'bodyType');
  const bodyName = readString(record, 'bodyName');
  if (
    childNodeId === undefined ||
    bodyType === undefined ||
    bodyName === undefined
  ) {
    return null;
  }
  return {
    id: childNodeId,
    type: bodyType,
    typeLabel: forEachBodyTypeLabel(bodyType),
    name: bodyName,
  };
}

function bodyAccent(bodyType: string): NodeAccent {
  switch (bodyType) {
    case 'agent':
    case 'ad-hoc-agent':
      return 'agent';
    case 'tool-call':
      return 'tool';
    case 'filter':
    case 'for-each':
      return 'control';
    default:
      return 'neutral';
  }
}

function BodyTypeIcon({
  bodyType,
  className,
}: {
  bodyType: string;
  className?: string;
}): React.ReactNode {
  switch (bodyType) {
    case 'agent':
    case 'ad-hoc-agent':
      return <Bot className={className} />;
    case 'tool-call':
      return <Wrench className={className} />;
    case 'filter':
      return <Filter className={className} />;
    case 'for-each':
      return <Repeat className={className} />;
    default:
      return null;
  }
}

function ForEachBodyBadge({
  body,
}: {
  body: ForEachBodySummary | null;
}): React.ReactNode {
  if (body === null) {
    return (
      <Text.Caption className="text-muted-foreground block">
        No loop body yet
      </Text.Caption>
    );
  }
  const accent = bodyAccent(body.type);
  const style = NODE_ACCENTS[accent];
  return (
    <Badge variant={style.badge} size="sm" className="max-w-full">
      <BodyTypeIcon bodyType={body.type} className="h-3 w-3 shrink-0" />
      <span className="truncate">{body.name}</span>
    </Badge>
  );
}

export const forEachNodeStrategy: WorkflowNodeRenderStrategy = {
  nodeType: WorkflowNodeType.ForEach,
  accent: 'control',
  badge: 'Loop',
  getTitle: () => 'For each',
  getDescription: (node) => describeForEachItems(node.data),
  renderIcon: () => <Repeat className="h-4 w-4" />,
  renderBody: (node) => {
    const body = bodySummaryFromNodeData(node.data);
    return (
      <div className="space-y-2">
        <Text.Caption className="text-muted-foreground block truncate">
          {describeForEachItems(node.data)}
        </Text.Caption>
        <ForEachBodyBadge body={body} />
      </div>
    );
  },
};

/**
 * Canvas card for for-each: embeds the loop-body as a single accent badge
 * (icon + name) so the body node itself can stay off the graph.
 */
export const ForEachNodeCard: React.FC<WorkflowGraphNodeFlowRenderProps> = ({
  node,
  className,
}) => {
  const body = bodySummaryFromNodeData(node.data);

  return (
    <WorkflowNodeShell
      title="For each"
      description={describeForEachItems(node.data)}
      icon={<Repeat className="h-4 w-4" />}
      accent="control"
      badge="Loop"
      className={className}
    >
      <ForEachBodyBadge body={body} />
    </WorkflowNodeShell>
  );
};
